/**
 * Árvore de rotas da aplicação.
 *
 * A URL é o estado de navegação: qual casca está na tela (vitrine, login ou
 * curso) e, dentro do curso, qual tela. Antes isso vivia num `useState<View>`
 * do App.tsx e numa chave de localStorage — por isso não havia link para uma
 * aula, botão voltar nem analytics por página.
 *
 * O que este arquivo NÃO sabe: quais telas um curso tem. Isso é declarado pelo
 * manifesto de cada curso e resolvido dentro do CourseApp.
 */

import React, { Suspense, useCallback } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { useAuth } from './contexts/AuthContext';
import { COURSE_ACCESS_DENIED_EVENT, getMyCourses } from './services/authService';
import { findCourseBySlug } from './config/courses';
import { hasCoursePackage, loadCoursePackage } from './courses/_registry';
import { ensureLocale } from './config/i18n';
import FullScreenLoader from './components/shared/FullScreenLoader';
import PlatformHome from './components/platform/PlatformHome';

// A home da plataforma é pública, então para o visitante anônimo o Auth e tudo
// que vive dentro de um curso seriam peso morto no chunk de entrada.
const Auth = React.lazy(() => import('./components/Auth'));
// Lazy: mantém os ~40KB de markdown dos documentos legais fora do bundle inicial.
const LegalConsentGate = React.lazy(
  () => import('./components/legal/LegalConsentGate'),
);

/**
 * Componente do curso, ligado ao manifesto dele.
 *
 * O React.lazy precisa ser estável entre renders — recriá-lo remontaria a
 * árvore inteira a cada render do guarda. Daí o cache por slug.
 */
const courseApps = new Map<string, React.ComponentType>();

function courseAppFor(slug: string): React.ComponentType {
  const cached = courseApps.get(slug);
  if (cached) return cached;

  const LazyCourse = React.lazy(async () => {
    const [{ default: CourseApp }, manifest] = await Promise.all([
      import('./components/CourseApp'),
      loadCoursePackage(slug),
    ]);
    // O idioma é do curso: carregar as traduções antes de montar evita que a
    // tela apareça com as chaves cruas por um frame.
    await ensureLocale(manifest.locale);
    return { default: () => <CourseApp manifest={manifest} /> };
  });

  courseApps.set(slug, LazyCourse);
  return LazyCourse;
}

/**
 * Casca do curso: confere o acesso e entrega a tela ao pacote do curso.
 *
 * Aqui morava a reconciliação de matrícula — `getMyCourses()` seguido de
 * `switchCourse()`, duas requisições e o descarte de todo o cache, em toda
 * entrada no curso. Isso deixou de existir: o token identifica a pessoa, o curso
 * vai no header de cada requisição, e trocar de curso é navegar.
 *
 * A conferência que sobrou é só para a tela não piscar: a lista de matrículas já
 * está no cache da vitrine, e quando não está, quem barra é o servidor — ele
 * responde 403 a um curso sem matrícula ativa, e nenhuma decisão de acesso
 * depende deste componente.
 */
const CourseShell: React.FC<{ slug: string; topicId: number }> = ({
  slug,
  topicId,
}) => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // O backend negou o acesso a este curso no meio do uso (prazo vencido,
  // bloqueio). Recarregar a lista de matrículas basta: com ela atualizada, o
  // `isBlocked` abaixo manda para a vitrine, que explica o motivo no card.
  React.useEffect(() => {
    const onDenied = (event: Event) => {
      if ((event as CustomEvent<{ topicId: number }>).detail?.topicId !== topicId) return;
      void queryClient.invalidateQueries({ queryKey: ['auth', 'my-courses'] });
    };
    window.addEventListener(COURSE_ACCESS_DENIED_EVENT, onDenied);
    return () => window.removeEventListener(COURSE_ACCESS_DENIED_EVENT, onDenied);
  }, [queryClient, topicId]);

  const { data: courses, isLoading } = useQuery({
    queryKey: ['auth', 'my-courses'],
    queryFn: getMyCourses,
    enabled: isAuthenticated,
    // Mesma chave e mesmo tempo da vitrine: entrar num curso a partir dela não
    // dispara requisição nenhuma.
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading && !courses) {
    return <FullScreenLoader label={t('common.loading')} />;
  }

  const enrollment = courses?.find((course) => course.topicId === topicId);

  // Sem matrícula, ou com ela suspensa, a vitrine é o destino — é lá que o aviso
  // aparece. Se a lista não carregou, segue em frente: o 403 do servidor é a
  // rede de segurança, e um blip de rede não pode trancar quem tem acesso.
  if (courses && (!enrollment || enrollment.isBlocked)) {
    return <Navigate to="/" replace />;
  }

  const CourseApp = courseAppFor(slug);

  return (
    <Suspense fallback={<FullScreenLoader label={t('common.loading')} />}>
      <CourseApp />
    </Suspense>
  );
};

/** Guarda de `/curso/:slug`: sem curso publicado ou sem sessão, ninguém entra. */
const CourseRoute: React.FC = () => {
  const { slug = '' } = useParams();
  const { isAuthenticated, user } = useAuth();
  const course = findCourseBySlug(slug);

  // Slug fora do catálogo, ou sem pacote registrado: não há curso para abrir.
  if (!course?.topicId || !hasCoursePackage(slug)) {
    return <Navigate to="/" replace />;
  }

  // Sem sessão não existe curso na tela. O curso pretendido volta pela URL
  // depois do login, no lugar do antigo `pendingTopicId` em localStorage.
  if (!isAuthenticated || !user) {
    return <Navigate to={`/entrar?curso=${encodeURIComponent(slug)}`} replace />;
  }

  return <CourseShell slug={slug} topicId={course.topicId} />;
};

/** `/entrar`, com o curso pretendido em `?curso=<slug>`. */
const LoginRoute: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const intended = findCourseBySlug(searchParams.get('curso') ?? '');

  // Já autenticado: o destino está na URL. Se a sessão for de outra matrícula,
  // quem troca é o guarda do curso.
  if (isAuthenticated) {
    return <Navigate to={intended ? `/curso/${intended.slug}` : '/'} replace />;
  }

  return (
    <Suspense fallback={<FullScreenLoader label={t('common.loading')} />}>
      <Auth onBack={() => navigate('/')} intendedTopicId={intended?.topicId} />
    </Suspense>
  );
};

/** A vitrine é a porta de entrada e funciona sem sessão. */
const PlatformRoute: React.FC = () => {
  const navigate = useNavigate();

  const enterCourse = useCallback(
    (slug: string) => navigate(`/curso/${slug}`),
    [navigate],
  );

  const requestLogin = useCallback(
    (slug?: string) =>
      navigate(slug ? `/entrar?curso=${encodeURIComponent(slug)}` : '/entrar'),
    [navigate],
  );

  return (
    <PlatformHome
      onEnterCourse={enterCourse}
      onRequestLogin={requestLogin}
      onOpenAdmin={(slug) => navigate(`/curso/${slug}/admin`)}
      onOpenSettings={(slug) => navigate(`/curso/${slug}/settings`)}
    />
  );
};

export const AppRoutes: React.FC = () => {
  const { isLoading } = useAuth();
  const { t } = useTranslation();

  // Enquanto o checkAuth() do AuthContext não termina, nenhuma rota decide
  // nada: `isAuthenticated` ainda é falso por omissão, e deixar um guarda
  // rodar aqui derrubaria para a vitrine quem deu F5 dentro do curso.
  if (isLoading) return <FullScreenLoader label={t('common.loading')} large />;

  return (
    <>
      <Routes>
        <Route path="/" element={<PlatformRoute />} />
        <Route path="/entrar" element={<LoginRoute />} />
        <Route path="/curso/:slug/*" element={<CourseRoute />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {/* Irmão das rotas, não guarda antes delas: o modal de aceite se sobrepõe
          à vitrine e a qualquer tela de curso (inclusive a ADM), e qualquer
          falha ao consultar o status resulta em nada renderizado — nunca em
          acesso bloqueado. Fica no QueryClient raiz, que o AuthContext limpa no
          logout. O boundary próprio estende isso às falhas de RENDER e de carga
          do chunk: sem ele, o erro subiria até o boundary da raiz e trocaria a
          aplicação inteira pela tela de erro (no posexpert, em 30/09/2026, com
          navegadores antigos). */}
      <Sentry.ErrorBoundary
        fallback={<></>}
        beforeCapture={(scope) => scope.setTag('area', 'legal-consent')}
      >
        <Suspense fallback={null}>
          <LegalConsentGate />
        </Suspense>
      </Sentry.ErrorBoundary>
    </>
  );
};

export default AppRoutes;
